import React, { useState } from 'react';
import { string, oneOfType, number } from 'prop-types';
import styled from 'styled-components';
import { withApollo } from 'react-apollo';
import * as Yup from 'yup';
import { Switch } from 'antd';
import { get, has, includes, map, orderBy, filter, forEach, isEmpty, size, head } from 'lodash';
import moment from 'moment';
import { Field, Formik, Form } from 'formik';
import { _get, thirdPartyCompanies, validateRut, generateTraceabilityUtil, sameDataComparator, employeeType, statusUser, createSalesChannelsPayload, userModuleTypes } from '../../../utils/misc';
import GetABMGroups from '../../graphql/getABMGroups';
import { GetSalesChannels } from '../../graphql';
import RESEND_CONFIRMATION_CODE from '../../graphql/resendConfirmationCode';
import withFirebase from '../../../hoc/withFirebase';
import { USER_STATUS } from '../../../utils/misc'
import {
    HasPermissions,
    FlexContainer,
    ConditionalRender,
    Text,
    Loading,
    FormikSelect,
    FormikInput,
    Row,
    Divider,
    Button,
    SalesChannelSelect,
    FormikCheckbox
} from '../../common';

const Label = styled.label`
  color: #333333;
  font-size: 12px;
  font-weight: 600;
`;

const objectValidationSchema = (tag, description, condition = 0) => Yup.object()
    .test(tag, description,
    function (value) {
        return size(value) > condition;
    })
    .typeError(description)
    .required('form.validations.required');

    const emailSpecialCharValidation = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const ValidationSchema = ({ onlyShoppers }) => Yup.object().shape({
    firstName: Yup.string()
        .min(1)
        .required('form.validations.required'),
    lastName: Yup.string()
        .min(1)
        .required('form.validations.required'),
    document: Yup.string()
        .test('test-name', 'El RUT ingresado no es válido',
            function (value) {
                return validateRut(value);
            })
        .required('form.validations.required'),
    phone: Yup.string()
        .min(9, 'form.validations.phone_length.min')
        .max(9, 'form.validations.phone_length.max'),
    email: Yup.string()
        .email('form.validations.email')
        .matches(emailSpecialCharValidation, 'El email contiene caracteres no permitidos')
        .required('form.validations.required'),
    company: objectValidationSchema('test-company', 'Debe seleccionar una empresa'),
    salesChannels: Yup.array().required('form.validations.required'),
    typeUser: objectValidationSchema('test-typeUser', 'Debe seleccionar un tipo de empleado'),
    profiles: onlyShoppers ? Yup.string().required('form.validations.required') : objectValidationSchema('test-profiles', 'Debe seleccionar un perfil'),
    statusUser: objectValidationSchema('test-statusUser', 'Debe seleccionar un estado')

});

const FormikInputGroups = ({ filterGroups, ...props }) => {
    return (
        <GetABMGroups>
            {({ data, loading }) => (
                <Loading loading={loading}>
                    <FormikSelect
                        {...props}
                        showArrow
                        mode='multiple'
                        optionsUp
                        optionFilterProp='alias'
                        options={map(
                            isEmpty(filterGroups) ? data : filter(data, ({ alias }) => includes(filterGroups, alias)),
                            ({ id, alias }) => ({ value: id, label: alias })
                        )}
          />
                </Loading>
            )}
        </GetABMGroups>
    );
};

const AutoData = ({ title, desc }) => (
    <>
        <Row>
            <Text semibold text={title} />
        </Row>
        <Row>
            <Text text={desc} />
        </Row>
    </>
);

AutoData.propTypes = {
    title: string,
    desc: oneOfType([number, string])
};

const CreateUserDisplayForm = ({ isSubmitting, isValid, handleReset, handleSubmit, setFieldValue, values, type, onlyShoppers, permission, client, abmOperUserAllowed, userModuleType, securityDisabled, formDisabled, ...props }) => {
    const [loadingResend, setLoadingResend] = useState();
    const [showErrorResend, setShowErrorResend] = useState(false);
    const [showSuccessResend, setShowSuccessResend] = useState(false);
    const [showMessage, setShowMessage] = useState(false);
    const disableForOnlyShoppers = onlyShoppers && (size(get(values, 'profiles', [])) > 1);
    const disableForOnlyUserSalesChannels = userModuleType === userModuleTypes[3];

    return (
        <Form onSubmit={handleSubmit} onReset={handleReset} style={{ width: '100%', flex: 1 }}>
            {!includes(['create'], type) && <>
                <FlexContainer justify='flex-end' direction='row'>
                    <Button text='Reenviar código' onClick={() => {
                        setShowMessage(false);
                        setLoadingResend(true);
                        return client.query({ query: RESEND_CONFIRMATION_CODE, variables: { email: get(values, 'email') }, fetchPolicy: 'network-only' })
                        .then(({ data }) => {
                            setShowMessage(true);
                            const internalCode = get(data, 'resendConfirmationCode.internalCode');
                            isEmpty(internalCode)
                                ? setShowErrorResend(true)
                                : setShowSuccessResend(true);
                            setLoadingResend(false);
                        });
                    }} width={150} loading={loadingResend} />
                </FlexContainer>
                {showMessage && <FlexContainer justify='flex-end'>
                    {showErrorResend && <Text text='Error al enviar código de confirmación' color='#d60101' />}
                    {showSuccessResend && <Text text='Código de confirmación enviado exitosamente' color='#1fa02e' />}
                </FlexContainer>}
            </>}
            { map([
                { label: 'Nombre', name: 'firstName' },
                { label: 'Apellido', name: 'lastName' },
                { label: 'RUT', name: 'document', isDocumentId: true, maxLength: 12, disabled: securityDisabled },
                { label: 'Mail', name: 'email', disabled: securityDisabled },
                { label: 'Teléfono', name: 'phone', codePrefix: '+56', maxLength: 9 },
                { label: 'Empresa', name: 'company', options: orderBy(thirdPartyCompanies(), 'label', 'asc'), component: FormikSelect },
                {
                    label: 'Sala',
                    name: 'salesChannels',
                    multiple: true,
                    allSalesChannels: !onlyShoppers,
                    disabled: get(values, 'switchAutoSala', false),
                    allSelected: get(values, 'switchAutoSala', false),
                    component: SalesChannelSelect
                },
                {
                    label: 'Todas las Salas',
                    name: 'switchAutoSala',
                    onChange: (value) => !value && setFieldValue('salesChannels', []),
                    component: FormikCheckbox,
                    disabled: false,
                    hideOnOnlyShoppers: onlyShoppers
                },
                {
                    label: 'Tipo de empleado',
                    name: 'typeUser',
                    options: employeeType,
                    component: FormikSelect
                },
                {
                    label: 'Perfil',
                    name: 'profiles',
                    position: 'relative',
                    filterGroups: abmOperUserAllowed,
                    component: FormikInputGroups,
                    hideOnOnlyShoppers: onlyShoppers
                },
                {
                    label: 'Estado',
                    name: 'statusUser',
                    options: statusUser,
                    component: FormikSelect
                },
                (includes([userModuleTypes[1]], userModuleType) && {
                    label: 'Splitero',
                    name: 'splitUser',
                    key: 'splitUser',
                    component: (value) => {
                        return <Row paddingVertical={10} gap={10} align='center'>
                            <HasPermissions to={['shoppers-page:create-splitero']} >
                                <Label>Splitero</Label>
                                <Switch
                                    checked={get(value, 'field.value') === 1}
                                    onChange={(value) => {
                                        setFieldValue('splitUser', value ? 1 : 0);
                                    }}
                                />
                            </HasPermissions>
                        </Row>;
                    }
                })
            ], ({ component, disabled, hideOnOnlyShoppers, ...elements }, index) => {
                if (hideOnOnlyShoppers) return null;
                return (
                    <Field
                        key={index}
                        component={component || FormikInput}
                        borderColor={disableForOnlyUserSalesChannels && !includes(['switchAutoSala', 'salesChannels'], get(elements, 'name')) ? '#ffffff' : '#eaeaea'}
                        disabled={formDisabled || (disabled !== false && (disabled || disableForOnlyUserSalesChannels))}
                        {...elements}
                    />
                );
            }) }
            <ConditionalRender condition={get(values, 'id')}>
                <AutoData title='Fecha de creación' desc={_get(values, 'createdAt') ? moment(_get(values, 'createdAt')).format('DD-MM-YYYY HH:mm:ss') : '-'} />
                <Divider />
                <AutoData title='Fecha de modificación' desc={_get(values, 'updatedAt') ? moment(_get(values, 'updatedAt')).format('DD-MM-YYYY HH:mm:ss') : '-'} />
                <Divider />
                <AutoData title='Fecha de última conexión' desc={_get(values, 'lastConnection') ? moment(_get(values, 'lastConnection')).format('DD-MM-YYYY HH:mm:ss') : '-'} />
            </ConditionalRender>
            <Divider top='10' bottom='70' />
            <FlexContainer
                style={{ bottom: 0, width: '100%', left: 0, borderTop: '1px solid #eaeaea' }}
                padding={18}
                direction='column'
            >
                <HasPermissions
                    isDisabled={includes('create', type)}
                    to={[`${permission}-page:edit`]}
                >
                    { (disableForOnlyShoppers || formDisabled) ? (
                        <FlexContainer>
                            <Text text='Modifique este usuario en el Módulo Usuarios' color='#d60101' />
                        </FlexContainer>
                    ) : null }
                    <FlexContainer justify='flex-end'>
                        <Button link text='Cancelar' htmlType='reset' width={100} />
                        <Button text='Guardar' htmlType='submit' disabled={disableForOnlyShoppers || formDisabled || isSubmitting} loading={isSubmitting} width={100} />
                    </FlexContainer>
                </HasPermissions>
            </FlexContainer>
        </Form>
    );
};

class CreateUserForm extends React.Component {
    shoppersProfileId = [{ label: 'shoppers', value: sessionStorage.getItem('SHOPPERS_PROFILE_ID') }];


  handleSubmit = (values) => {
        
        const { onlyShoppers, userStatus, toggleModalAlert, onReset, type } = this.props;

        if (onlyShoppers && type === "create") {
          const email = get(values, 'email');
          userStatus(email).then(users => {
            if (users && users.length > 0) {
                const statusUser = get(users[0], 'statusUser', null);
                if (statusUser == USER_STATUS.ACTIVE) {
                    toggleModalAlert("error", "Usuario ya existe, comunícate con el administrador")
                } else {
                    toggleModalAlert("error", "Usuario inactivo, comunícate con el administrador")
                }
                onReset()
                return;
            }
            this.proceedWithCreation(values);
          });
          return;
        }

        this.proceedWithCreation(values);
    };

  proceedWithCreation = (values) => {
      const { onSubmit, onSubmitSuccess, salesChannels, initialValues, database, onlyShoppers, client, type } = this.props;
      const createMode = type === 'create';

      const traceOnCreateUser = async () => {
        if (createMode && !onlyShoppers) {
            await generateTraceabilityUtil(client, { actionId: 1, idUser: get(values, 'email') });
        }
      };

      const onChangeStatusUser = async () => {
          const initial = get(initialValues, 'statusUser');
          const now = get(values, 'statusUser.value');
          if (createMode || (initial === now)) return;
          await generateTraceabilityUtil(client,
            {
                actionId: now === '0' ? 4 : 3,
                idUser: get(initialValues, 'email'),
                beforeChange: `'statusUser': '${initial}'`,
                afterChange: `'statusUser': '${now}'`
            });
        };

      const newSalesChannels = onlyShoppers ? [...get(values, 'salesChannels', []), ...get(initialValues, 'hiddenSalesChannel', [])] : get(values, 'salesChannels', []);

      const profiles = onlyShoppers ? this.shoppersProfileId : [get(values, 'profiles', head(this.shoppersProfileId))];

        const onChangeUserProfiles = async () => {
            const beforeChange = head(get(initialValues, 'profiles', ''));
            const afterChange = get(head(profiles), 'label', '');
            if (!createMode && !sameDataComparator(beforeChange, afterChange)) {
                await generateTraceabilityUtil(client,
                    {
                        actionId: 10,
                        idUser: get(initialValues, 'email'),
                        beforeChange: `'profiles': '${beforeChange}'`,
                        afterChange: `'profiles': '${afterChange}'`
                    });
            };
        };

        const objectFieldCleanerAndExtractor = field => filter(map(field, ({ value }) => value), e => !isEmpty(e));

        // This reducer helps to create traceability if any field value has beend changed on submit
        const traceOnFieldChange = async () => {
            if (createMode) return;
            forEach(values, async (value, index) => {
                // Trace on company or employee type changes
                if (includes(['company', 'typeUser'], index) && !sameDataComparator(get(initialValues, index), get(value, 'value'))) {
                    await generateTraceabilityUtil(
                        client,
                        {
                            actionId: 9,
                            idUser: get(initialValues, 'email'),
                            beforeChange: `'${index}': '${get(initialValues, index)}'`,
                            afterChange: `'${index}': '${get(value, 'value')}'`
                        });
                }
                // Trace if type of value is array or object, and if sales channel field is changed
                if (includes(['array', 'object'], typeof value) && (index === 'salesChannels') && !sameDataComparator(objectFieldCleanerAndExtractor(get(initialValues, index)), objectFieldCleanerAndExtractor(value))) {
                    await generateTraceabilityUtil(
                        client,
                        {
                            actionId: 9,
                            idUser: get(initialValues, 'email'),
                            beforeChange: `'${index}': '${objectFieldCleanerAndExtractor(get(initialValues, index))}'`,
                            afterChange: `'${index}': '${objectFieldCleanerAndExtractor(value)}'`
                        });
                }
                // Trace if type of value is string. As firstname, lastname, phone, etc.
                if (includes(['string'], typeof value) && !sameDataComparator(get(initialValues, index), value)) {
                    await generateTraceabilityUtil(
                        client,
                        {
                            actionId: 9,
                            idUser: get(initialValues, 'email'),
                            beforeChange: `'${index}': '${get(initialValues, index)}'`,
                            afterChange: `'${index}': '${value}'`
                        });
                };
            });
            // Trace on profile changes
            await onChangeUserProfiles();
            await onChangeStatusUser();
        };

      const salesChannelsPayload = has(initialValues, 'id')
          ? createSalesChannelsPayload(salesChannels, newSalesChannels)
          : createSalesChannelsPayload(newSalesChannels, newSalesChannels);

          const { hiddenSalesChannel = [], ...finalValues } = values;

          const payload = onlyShoppers ? {
                ...finalValues,
                salesChannels: salesChannelsPayload,
                profiles
            } : { ...finalValues, salesChannels: salesChannelsPayload, profiles };

      Promise.all([
          onSubmit(payload)
      ]).then(async (response) => {
          const repeatedRut = get(response, '[0].data.updatePicker.repeatedRut')
          this.props.setVisibleModalRepeatUser(repeatedRut)
          const userId = btoa(initialValues.id);
          database.ref('changeUser/' + userId).set({
              updated: new Date().toISOString()
          });
          await traceOnCreateUser();
          await traceOnFieldChange();
          return onSubmitSuccess();
      }).catch(console.error);
  }

  handleReset = () => {
      const { onReset } = this.props;
      console.log('should reset');
      onReset();
  }

  _defaultInitialValues = () => {
      const { onlyShoppers, type } = this.props;
      const defaultValueIfOnlyShopper = onlyShoppers ? '0' : '';
      return {
          document: '',
          email: '',
          firstName: '',
          lastName: '',
          company: '',
          phone: '',
          profiles: onlyShoppers && includes(['create'], type) ? ['shoppers'] : [],
          salesChannels: [],
          status: 4,
          statusUser: defaultValueIfOnlyShopper,
          switchAutoSala: false,
          typeUser: defaultValueIfOnlyShopper
      };
  }

  _getInitialValues = () => {
      const { initialValues, salesChannels } = this.props;
      return has(initialValues, 'id') ? initialValues : this._defaultInitialValues();
  }

  render () {
      const { type, onlyShoppers, permission, client, abmOperUserAllowed, userModuleType, initialValues } = this.props;
      
      var securityDisabled = false
      var formDisabled = false
      if(onlyShoppers && type === "detail"){
        securityDisabled = true
        if(get(initialValues, 'statusUser', 0) == USER_STATUS.INACTIVE){
            formDisabled = true
        }
      }

      const createUserDisplayFormProps = {
          type,
          onlyShoppers,
          permission,
          client,
          abmOperUserAllowed,
          userModuleType,
          securityDisabled,
          formDisabled
      };
      return (
          <Formik
              enableReinitialize
              onSubmit={this.handleSubmit}
              onReset={this.handleReset}
              validationSchema={ValidationSchema({ onlyShoppers })}
              initialValues={this._getInitialValues()}
              render={props => <CreateUserDisplayForm {...props} {...createUserDisplayFormProps} />}
      />
      );
  }
}

CreateUserForm.defaultProps = {
    initialValues: {},
    onSubmit: (payload) => (Promise.resolve(payload)),
    onSubmitSuccess: () => console.log('onSubmitSuccess'),
    onReset: () => console.log('should reset')
};

export default withApollo(withFirebase((props) => (
    <GetSalesChannels allSalesChannels>
        {({ data, loading }) => (
            <Loading loading={loading}>
                <CreateUserForm {...props} salesChannels={data} />
            </Loading>
        )}
    </GetSalesChannels>
)));
